import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import api from '../services/api';
import { SYSTEMS } from '../config/systems';

const UnitSettingsContext = createContext();

export const useUnitSettings = () => {
  const context = useContext(UnitSettingsContext);
  if (!context) {
    throw new Error('useUnitSettings must be used within a UnitSettingsProvider');
  }
  return context;
};

const ALL_HYDRO_UNITS = SYSTEMS.map(({ id, name }) => ({ id, name }));

// The last settings seen, so a page opened on a fresh load fetches for the right
// units first time instead of fetching for "all enabled" and again once
// /settings/units answers.
const STORAGE_KEY = 'unitSettings';

const readCachedSettings = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

export const UnitSettingsProvider = ({ children }) => {
  const [unitSettings, setUnitSettings] = useState(readCachedSettings);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get('/settings/units');
      if (response.data.units) {
        setUnitSettings(response.data.units);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(response.data.units));
      }
    } catch (error) {
      console.log('Using default unit settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Pages key their fetch effects on isUnitEnabled / isUnitActive. Those must only
  // change identity when a unit's enabled or maintenance flag actually changes —
  // not whenever /settings/units hands back an equal object — or every page
  // fetches its data twice on load.
  const statusKey = ALL_HYDRO_UNITS.map(({ id }) => {
    const settings = unitSettings[id];
    return `${id}:${settings?.enabled !== false ? 1 : 0}${settings?.maintenance === true ? 1 : 0}`;
  }).join('|');

  const status = useMemo(() => Object.fromEntries(
    statusKey.split('|').map((part) => {
      const [id, flags] = part.split(':');
      return [id, { enabled: flags[0] === '1', maintenance: flags[1] === '1' }];
    })
  ), [statusKey]);

  // Check if a unit is enabled
  const isUnitEnabled = useCallback((unitId) => {
    return status[unitId]?.enabled !== false; // Default to true if not set
  }, [status]);

  // Check if a unit is in maintenance mode
  const isUnitInMaintenance = useCallback((unitId) => {
    return status[unitId]?.maintenance === true;
  }, [status]);

  // Check if a unit is active (enabled and not in maintenance for data fetching)
  const isUnitActive = useCallback((unitId) => {
    return isUnitEnabled(unitId) && !isUnitInMaintenance(unitId);
  }, [isUnitEnabled, isUnitInMaintenance]);

  // Get filtered list of enabled units
  const getEnabledUnits = useCallback(() => {
    return ALL_HYDRO_UNITS.filter(unit => isUnitEnabled(unit.id));
  }, [isUnitEnabled]);

  // Get all units with their status
  const getUnitsWithStatus = useCallback(() => {
    return ALL_HYDRO_UNITS.map(unit => ({
      ...unit,
      enabled: isUnitEnabled(unit.id),
      maintenance: isUnitInMaintenance(unit.id),
      active: isUnitActive(unit.id)
    }));
  }, [isUnitEnabled, isUnitInMaintenance, isUnitActive]);

  // Refresh settings (can be called after saving)
  const refreshSettings = useCallback(() => {
    fetchSettings();
  }, [fetchSettings]);

  return (
    <UnitSettingsContext.Provider value={{
      unitSettings,
      loading,
      isUnitEnabled,
      isUnitInMaintenance,
      isUnitActive,
      getEnabledUnits,
      getUnitsWithStatus,
      refreshSettings,
      ALL_HYDRO_UNITS
    }}>
      {children}
    </UnitSettingsContext.Provider>
  );
};

export default UnitSettingsContext;
